export const hud = () => ({
  // Initialize HUD component
  init() {
    console.log('HUD component initialized');
    // Only start if we have player data
    if (this.$store.app.hasPlayer()) {
      this.startStatusUpdates();
      
      // Listen for logout to cleanup
      window.addEventListener('app:logout', () => this.destroy());
    }
  },
  
  // Status update interval
  statusInterval: null,
  
  // Start periodic status updates
  startStatusUpdates() {
    this.statusInterval = setInterval(() => {
      this.updateStatuses();
    }, 1000); // Update every second
  },
  
  // Cleanup method
  destroy() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
      console.log('HUD timers cleaned up');
    }
  },
  
  // Update player statuses (degradation over time)
  updateStatuses() {
    const store = this.$store.app;
    
    // Exit early if no player data to prevent null access crashes
    if (!store.hasPlayer()) return;
    
    const now = Date.now();
    const timeDiff = now - store.game.lastUpdate;
    
    // Only update if enough time has passed (1 second minimum)
    if (timeDiff < 1000) return;
    
    // Calculate degradation per minute
    const minutesPassed = timeDiff / (1000 * 60);
    
    // Degrade thirst and hunger over time
    const thirstDegradation = 0.5 * minutesPassed; // 0.5% per minute
    const hungerDegradation = 0.3 * minutesPassed; // 0.3% per minute
    
    // Apply degradation
    let newThirst = Math.max(0, store.player.thirst - thirstDegradation);
    let newHunger = Math.max(0, store.player.hunger - hungerDegradation);
    
    // Health degradation when thirst/hunger are too low
    let newHealth = store.player.health;
    if (newThirst < 20 || newHunger < 20) {
      const healthDegradation = 0.2 * minutesPassed;
      newHealth = Math.max(0, newHealth - healthDegradation);
    }
    
    // Update store
    store.updatePlayer({
      health: Math.round(newHealth * 100) / 100,
      thirst: Math.round(newThirst * 100) / 100,
      hunger: Math.round(newHunger * 100) / 100
    });
    
    store.game.lastUpdate = now;
    
    // Check for critical conditions
    this.checkCriticalConditions();
  },
  
  // Check for critical health/status conditions
  checkCriticalConditions() {
    const store = this.$store.app;
    const { health, thirst, hunger } = store.player;
    
    // Player KO condition
    if (health <= 0) {
      this.handlePlayerKO();
      return;
    }
    
    // Warning notifications
    if (thirst < 15 && !this.hasRecentNotification('thirst')) {
      store.addNotification('¡Necesitas beber agua urgentemente!', 'warning');
      this.setNotificationCooldown('thirst');
    }
    
    if (hunger < 15 && !this.hasRecentNotification('hunger')) {
      store.addNotification('¡Necesitas comer pronto!', 'warning');
      this.setNotificationCooldown('hunger');
    }
    
    if (health < 25 && !this.hasRecentNotification('health')) {
      store.addNotification('¡Tu salud está muy baja!', 'danger');
      this.setNotificationCooldown('health');
    }
  },
  
  // Notification cooldown system
  notificationCooldowns: {},
  
  hasRecentNotification(type) {
    const lastNotification = this.notificationCooldowns[type];
    return lastNotification && (Date.now() - lastNotification) < 30000; // 30 second cooldown
  },
  
  setNotificationCooldown(type) {
    this.notificationCooldowns[type] = Date.now();
  },
  
  // Handle player KO (unconscious)
  handlePlayerKO() {
    const store = this.$store.app;
    
    // Stop all active timers
    Object.keys(store.timers).forEach(type => {
      if (store.timers[type]) {
        store.clearTimer(type);
      }
    });
    
    // Reset stats to minimum viable levels
    store.updatePlayer({
      health: 25,
      thirst: 30,
      hunger: 30
    });
    
    // Penalty: lose some money
    const moneyLoss = Math.floor(store.player.money * 0.1); // 10% loss
    store.updatePlayer({
      money: Math.max(0, store.player.money - moneyLoss)
    });
    
    store.addNotification(`¡Te desmayaste! Perdiste ${moneyLoss} monedas.`, 'danger');
  },
  
  // Get status bar color based on percentage
  getStatusColor(value, type) {
    if (value > 70) return 'good';
    if (value > 30) return 'warning';
    return 'danger';
  },
  
  // Format currency display
  formatMoney(amount) {
    return new Intl.NumberFormat('es-ES').format(amount);
  },

  // XP Progression calculations
  calculateXpForNextLevel(level) {
    return Math.floor(100 * Math.pow(level, 1.35));
  },

  calculateTotalXpForLevel(level) {
    let totalXp = 0;
    for (let i = 1; i < level; i++) {
      totalXp += Math.floor(100 * Math.pow(i, 1.35));
    }
    return totalXp;
  },

  getCurrentLevelXp() {
    const store = this.$store.app;
    const player = store.player;
    const totalXpForCurrentLevel = this.calculateTotalXpForLevel(player.level || 1);
    return (player.xp || 0) - totalXpForCurrentLevel;
  },

  getNextLevelXp() {
    const store = this.$store.app;
    const player = store.player;
    return this.calculateXpForNextLevel(player.level || 1);
  },

  getXpProgress() {
    const currentLevelXp = this.getCurrentLevelXp();
    const nextLevelXp = this.getNextLevelXp();
    return Math.round((currentLevelXp / nextLevelXp) * 100);
  },
  
  // Status bar click handlers for tooltips/actions
  onHealthClick() {
    // Future: show health details or use health items
    console.log('Health clicked');
  },
  
  onThirstClick() {
    // Future: quick drink action
    console.log('Thirst clicked');
  },
  
  onHungerClick() {
    // Future: quick eat action
    console.log('Hunger clicked');
  },
  
  onMoneyClick() {
    // Future: show money details/transactions
    console.log('Money clicked');
  },
  
  // Cleanup when component is destroyed
  destroy() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
  }
});